import React from "react";
import { useForm, Controller } from "react-hook-form";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import Line from "../../../components/Line/Line";
import {
  useAddEmployeeJoiningDate,
  useAddEmployeeEndingDate,
} from "../../../queries/employeeQueries";
import { toast } from "react-toastify";
import { DatePicker } from "antd";
import dayjs from "dayjs";

const EditEmployementInformationModal = ({
  openModal,
  onCloseModal,
  employee,
  employeeJoiningDate,
  employeeEndDate,
}) => {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      joining_date: employeeJoiningDate
        ? dayjs(employeeJoiningDate)
        : null,
      ending_date: employeeEndDate
        ? dayjs(employeeEndDate)
        : null,
    },
  });

  const { mutate: addJoiningDate, isLoading: isJoiningLoading } =
    useAddEmployeeJoiningDate();
  const { mutate: addEndingDate, isLoading: isEndingLoading } =
    useAddEmployeeEndingDate();

  const onSubmit = (data) => {
    const hasJoining = !!data.joining_date;
    const hasEnding = !!data.ending_date;

    if (!hasJoining && !hasEnding) {
      toast.info("No changes made.");
      onCloseModal();
      return;
    }

    if (hasJoining) {
      addJoiningDate(
        {
          employee_id: employee?.employee_id,
          joining_date: data.joining_date.format("YYYY-MM-DD"),
        },
        {
          onSuccess: (res) => {
            if (res?.status) toast.success(res?.message || "Joining date saved!");
            onCloseModal();
          },
          onError: () => toast.error("Failed to save joining date."),
        }
      );
    }

    if (hasEnding) {
      addEndingDate(
        {
          employee_id: employee?.employee_id,
          ending_date: data.ending_date.format("YYYY-MM-DD"),
        },
        {
          onSuccess: (res) => {
            if (res?.status) toast.success(res?.message || "Ending date saved!");
            onCloseModal();
          },
          onError: () => toast.error("Failed to save ending date."),
        }
      );
    }
  };

  return (
    <Modal open={openModal} onClose={onCloseModal} center>
      <div className="p-5 w-[520px]">
        <h2 className="text-lg leading-6 text-[#1F2937] font-semibold mb-5">
          Add/Edit Employment Information
        </h2>

        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          {/* Joining Date */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-black">
              Join Date <span className="text-red-700">*</span>
            </label>
            <Controller
              name="joining_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  placeholder="Select Joining Date"
                  format="DD-MM-YYYY"
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  disabledDate={(current) =>
                    current && current > dayjs().endOf("day")
                  }
                  allowClear
                  className="h-10 w-full"
                />
              )}
            />
          </div>

          {/* End Date */}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-black">
              End Date
            </label>
            <Controller
              name="ending_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  placeholder="Select End Date"
                  format="DD-MM-YYYY"
                  value={field.value}
                  onChange={(date) => field.onChange(date)}
                  allowClear
                  className="h-10 w-full"
                />
              )}
            />
          </div>

          <Line />
          <Line />

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              className="btn btn--border text-sm font-medium"
              onClick={onCloseModal}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn text-sm font-medium"
              disabled={isJoiningLoading || isEndingLoading}
            >
              {isJoiningLoading || isEndingLoading
                ? "Saving..."
                : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditEmployementInformationModal;
